import React, { useEffect, useState } from 'react';

export interface DailyStatsData {
  date: string;
  count: number;
}

interface DailyStatsChartProps {
  data: DailyStatsData[];
  title: string;
  width?: number;
  height?: number;
  lineColor?: string;
  yAxisLabel?: string;
  tooltipSuffix?: string;
}

export const DailyStatsChart: React.FC<DailyStatsChartProps> = ({ 
  data, 
  title,
  width: propWidth, 
  height: propHeight,
  lineColor = "#3b82f6",
  yAxisLabel = "Count",
  tooltipSuffix = "items"
}) => {
  const [dimensions, setDimensions] = useState({ width: 800, height: 300 });

  // Responsive dimensions
  useEffect(() => {
    const updateDimensions = () => {
      const screenWidth = window.innerWidth;
      
      if (propWidth && propHeight) {
        // Use provided dimensions
        setDimensions({ width: propWidth, height: propHeight });
      } else if (screenWidth < 640) {
        // Mobile: smaller chart with extra height for rotated labels
        setDimensions({ width: Math.min(screenWidth - 32, 400), height: 230 });
      } else if (screenWidth < 1024) {
        // Tablet: medium chart
        setDimensions({ width: Math.min(screenWidth - 64, 600), height: 250 });
      } else {
        // Desktop: full size chart
        setDimensions({ width: 800, height: 300 });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [propWidth, propHeight]);

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 sm:h-64 text-gray-500 text-sm sm:text-base">
        No data available for {title.toLowerCase()}
      </div>
    );
  }

  const { width, height } = dimensions;
  const isMobile = width < 500;
  const padding = isMobile ? 50 : 60;
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;

  const maxCount = Math.max(...data.map(d => d.count));
  const minCount = 0;

  // Create points for the line
  const points = data.map((d, index) => {
    const x = padding + (index / (data.length - 1)) * chartWidth;
    const y = padding + chartHeight - ((d.count - minCount) / (maxCount - minCount || 1)) * chartHeight;
    return { x, y, ...d };
  });

  // Create path string for the line
  const pathData = points.reduce((path, point, index) => {
    const command = index === 0 ? 'M' : 'L';
    return `${path} ${command} ${point.x} ${point.y}`;
  }, '');

  // Y-axis labels (fewer on mobile)
  const yLabels: { value: number; y: number }[] = [];
  const labelCount = isMobile ? 3 : 5;
  
  // Create unique values for Y-axis labels
  const uniqueValues = new Set<number>();
  
  // Always include 0
  uniqueValues.add(0);
  
  // Add the maximum value
  if (maxCount > 0) {
    uniqueValues.add(maxCount);
  }
  
  // Add intermediate values
  for (let i = 1; i < labelCount; i++) {
    const value = Math.round((maxCount / labelCount) * i);
    if (value > 0 && value < maxCount) {
      uniqueValues.add(value);
    }
  }
  
  // Convert to sorted array and create label objects
  const sortedValues = Array.from(uniqueValues).sort((a, b) => a - b);
  sortedValues.forEach((value, index) => {
    const normalizedPosition = maxCount > 0 ? value / maxCount : 0;
    const y = padding + chartHeight - normalizedPosition * chartHeight;
    yLabels.push({ value, y });
  });

  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    if (isMobile) {
      // Shorter format for mobile
      return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="p-2 sm:p-2 rounded-lg border bg-transparent w-full overflow-x-auto">
      <h3 className="text-sm sm:text-lg font-semibold mb-2 sm:mb-4">{title}</h3>
      <div className="min-w-full">
        <svg width={width} height={height} className="overflow-visible max-w-full">
        {/* Grid lines */}
        {yLabels.map((label, index) => (
          <g key={index}>
            <line
              x1={padding}
              y1={label.y}
              x2={padding + chartWidth}
              y2={label.y}
              stroke="#f3f4f6"
              strokeWidth="1"
            />
            <text
              x={padding - 10}
              y={label.y + 4}
              textAnchor="end"
              className={isMobile ? "text-xs fill-gray-600" : "text-xs fill-gray-600"}
            >
              {label.value}
            </text>
          </g>
        ))}

        {/* X-axis */}
        <line
          x1={padding}
          y1={padding + chartHeight}
          x2={padding + chartWidth}
          y2={padding + chartHeight}
          stroke="#e5e7eb"
          strokeWidth="2"
        />

        {/* Y-axis */}
        <line
          x1={padding}
          y1={padding}
          x2={padding}
          y2={padding + chartHeight}
          stroke="#e5e7eb"
          strokeWidth="2"
        />

        {/* Y-axis label */}
        <text
          x={isMobile ? 15 : 20}
          y={padding + chartHeight / 2}
          textAnchor="middle"
          className={isMobile ? "text-xs fill-gray-700" : "text-sm fill-gray-700"}
          transform={`rotate(-90, ${isMobile ? 15 : 20}, ${padding + chartHeight / 2})`}
        >
          {yAxisLabel}
        </text>

        {/* Main line */}
        <path
          d={pathData}
          fill="none"
          stroke={lineColor}
          strokeWidth="2"
          className="drop-shadow-sm"
        />

        {/* Data points */}
        {points.map((point, index) => (
          <g key={index}>
            <circle
              cx={point.x}
              cy={point.y}
              r={isMobile ? "3" : "4"}
              fill={lineColor}
              className="drop-shadow-sm"
            />
            {/* Tooltip on hover */}
            <circle
              cx={point.x}
              cy={point.y}
              r={isMobile ? "15" : "12"}
              fill="transparent"
              className="cursor-pointer"
            >
              <title>{`${formatDate(point.date)}: ${point.count} ${tooltipSuffix}`}</title>
            </circle>
          </g>
        ))}

        {/* X-axis labels (fewer on mobile with rotation) */}
        {points.map((point, index) => {
          const showLabel = isMobile 
            ? (index % 10 === 0 || index === points.length - 1)
            : (index % 5 === 0 || index === points.length - 1);
          
          if (showLabel) {
            return (
              <text
                key={index}
                x={point.x}
                y={padding + chartHeight + (isMobile ? 35 : 20)}
                textAnchor={isMobile ? "end" : "middle"}
                className="text-xs fill-gray-600"
                transform={isMobile ? `rotate(-45, ${point.x}, ${padding + chartHeight + 35})` : undefined}
              >
                {formatDate(point.date)}
              </text>
            );
          }
          return null;
        })}
      </svg>
      </div>
    </div>
  );
}; 